import { ExtendedChatMessage } from '../../store/chatStore'

export type MessageRole = 'user' | 'assistant'

export interface ChatMessage {
  role: MessageRole
  content: string
}

export interface Feedback {
  id: string
  sentiment: number
  sentimentDescription: string
  comment: string
  createdAt: number
  updatedAt: number
}

export interface Interaction {
  id: string | null
  role: MessageRole
  message: string
  citations: string[]
  feedback: Feedback | null
  createdAt: number
  updatedAt: number
}

export interface ISession {
  id: string | null
  title: string | null
  userId: string | null
  tenantId: string | null
  projectId: string | null
  interactions: Interaction[]
  createdAt: number | null
  updatedAt: number | null
}

export type StreamEventType = 'delta' | 'thinking' | 'thinking_done' | 'session_info' | 'done' | 'error'

export type ThinkingStepStatus = 'running' | 'done'

export interface ThinkingStep {
  step: string
  type: string
  status: ThinkingStepStatus
}

export interface StreamCallbacks {
  onChunk: (chunk: string) => void
  onThinking?: (step: ThinkingStep) => void
  onThinkingDone?: (durationMessage: string) => void
  onSessionInfo?: (sessionId: string) => void
  onDone?: () => void
  onError?: (error: Error) => void
}

export interface GetLatestSessionParams {
  userId: string
}

export interface CreateUserSessionParams {
  userId: string
}

export interface SendProjectMessageParams {
  userId: string
  tenantId: string
  projectId: string
  sessionId: string
  message?: string
}

export interface SendTenantMessageParams {
  userId: string
  tenantId: string
  sessionId: string
  message?: string
}

export const mapFeedbackResponse = (data: any): Feedback => ({
  id: data.id,
  sentiment: data.sentiment,
  sentimentDescription: data.sentiment_description,
  comment: data.comment,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
})

export const mapInteractionResponse = (data: any): Interaction => ({
  id: data.id,
  role: data.role,
  message: data.message,
  citations: data.citations || [],
  feedback: data.feedback ? mapFeedbackResponse(data.feedback) : null,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
})

export const mapSessionResponse = (data: any): ISession => ({
  id: data.id,
  title: data.title,
  userId: data.user_id,
  tenantId: data.tenant_id,
  projectId: data.project_id,
  interactions: (data.interactions || []).map(mapInteractionResponse),
  createdAt: data.created_at,
  updatedAt: data.updated_at,
})

export const mapInteractionsToMessages = (interactions: Interaction[]): ExtendedChatMessage[] => {
  return interactions.map((interaction) => ({
    id: interaction.id || `interaction-${interaction.createdAt}`,
    role: interaction.role as 'user' | 'assistant',
    content: interaction.message,
    date: new Date(interaction.createdAt * 1000).toISOString(),
    isLoading: false,
    isError: false,
    citations: interaction.citations,
  }))
}
